import type { Subscription } from '@packages/types';

/**
 * Returns true when a CANCELLING subscription's paid period has not yet
 * elapsed. Mirrors the backend `subscriptionGrantsWriteAccess` CANCELLING
 * arm, so `SubscriptionProvider`'s `isActive` mirror agrees with the write
 * gate on the far side of every mutation: access is retained for the length
 * of the paid period, or indefinitely when the period end is unknown.
 */
export function isCancellingWithinPeriod(subscription: Subscription): boolean {
  if (subscription.status !== 'CANCELLING') return false;
  return subscription.currentPeriodEnd == null || new Date(subscription.currentPeriodEnd) > new Date();
}

/**
 * True when the free within-period Renew affordance applies: a CANCELLING
 * subscription the backend has marked reactivatable (see
 * `isSubscriptionReactivatable` in `billing.service.ts` - paid period not yet
 * elapsed, and either there is no provider subscription to reconcile or the
 * customer's own cancel is why it stopped renewing).
 */
export function subscriptionCanRenew(subscription: Subscription | null): boolean {
  return !!subscription && subscription.status === 'CANCELLING' && subscription.reactivatable;
}

/**
 * Mirrors the backend's `isSubscriptionDeferrableOnCancel` (billing.service.ts):
 * a cancel only defers to CANCELLING - access retained, free renewal until the
 * period ends - when `currentPeriodEnd` is still ahead of now. Used by the
 * cancel confirmation dialog to decide whether it may promise that deferral.
 * A PAST_DUE row's period end is in the past by construction, so the cancel
 * affordance being open to PAST_DUE (see `canCancel` in `SubscriptionSection`)
 * does not by itself mean the deferral applies - promising it there would show
 * an already-elapsed date and then converge the subscription straight to
 * CANCELLED with no free renewal.
 */
export function subscriptionCancelWillDefer(subscription: Pick<Subscription, 'currentPeriodEnd'> | null): boolean {
  return !!subscription?.currentPeriodEnd && new Date(subscription.currentPeriodEnd) > new Date();
}

/**
 * True when the subscribe-again upsell should show: no subscription at all,
 * or one that has stopped renewing with nothing left to renew for free -
 * EXPIRED, TRIALING, CANCELLED, or CANCELLING past its free renewal window
 * (`subscriptionCanRenew` false).
 */
export function subscriptionShowsSubscribeUpsell(subscription: Subscription | null): boolean {
  if (!subscription) return true;
  if (subscription.status === 'EXPIRED' || subscription.status === 'TRIALING' || subscription.status === 'CANCELLED') return true;
  return subscription.status === 'CANCELLING' && !subscription.reactivatable;
}
